import React, { useEffect, useMemo, useState } from "react";
import GridContainer from "../../../components/Grid/GridContainer";
import GridItem from "../../../components/Grid/GridItem";
import Table from "../../../components/Table/Table.jsx";
import {
    formatDateOnly,
    formatDollar,
    formatPercentage
} from "../../../shared/utility.js";
import CustomInput from "../../../components/CustomInput/CustomInputValidate.jsx";
import { IconButton } from "@material-ui/core";
import { ArrowDownward, ArrowUpward, Cached } from "@material-ui/icons";
import axios_gci from "store/axios/axios-gci";

const TABLE_HEAD = [
    "Payee / Associate",
    "Type",
    "Date Lease Signed",
    "RS #",
    "Invoice Number",
    "Total Commission on RS",
    "Associate Side on RS",
    "Total Commission Associate Side",
    "Associate % Comm on RS",
    "Associate Comm on RS",
    "Associate Comm to Payout",
    "GCI Level",
    "GCI Threshold",
    "Pending Commission",
    "Commission Earned Year",
    "Commission Earned Year to Date",
    "Commission Earned",
    "GCI Rate",
    "House Payout"
];

const INVOICE_COLUMN_INDEX = 4;

const normalize = value => String(value == null ? "" : value).toLowerCase();

const parseSortValue = value => {
    if (value == null) return "";
    const text = String(value).trim();
    if (!text) return "";

    const dateMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dateMatch) {
        return new Date(
            Number(dateMatch[3]),
            Number(dateMatch[1]) - 1,
            Number(dateMatch[2])
        ).getTime();
    }

    const numericText = text.replace(/[$,%]/g, "").replace(/,/g, "");
    if (numericText !== "" && !Number.isNaN(Number(numericText))) {
        return Number(numericText);
    }

    return text.toLowerCase();
};

const compareRows = (a, b, columnIndex, direction) => {
    const valueA = parseSortValue(a[columnIndex]);
    const valueB = parseSortValue(b[columnIndex]);
    const multiplier = direction === "asc" ? 1 : -1;

    if (valueA === valueB) return 0;
    if (valueA === "") return 1;
    if (valueB === "") return -1;
    return valueA > valueB ? multiplier : -multiplier;
};

const formatRate = value => {
    if (value == null || value === "") return "";
    return formatPercentage(parseFloat(value) * 100);
};

const formatSourceType = value => {
    const labels = {
        associate: "Associate",
        outside_broker: "Outside Broker",
        referral: "Referral",
        presplit: "Presplit",
        gci_board_snapshot: "Board Snapshot"
    };
    return labels[value] || "Associate";
};

const createTableRow = row => [
    row.associate_name || "",
    formatSourceType(row.source_type),
    row.lease_signed_date ? row.lease_signed_date : "",
    row.rs_id || "",
    row.invoice_number || "",
    formatDollar(row.total_commission_on_rs),
    row.associate_side || "",
    formatDollar(row.total_commission_associate_side),
    formatRate(row.associate_percentage),
    formatDollar(row.associate_commission_on_rs),
    formatDollar(row.associate_commission_to_payout),
    row.ending_gci_level == null ? "" : row.ending_gci_level,
    formatDollar(row.gci_threshold),
    formatDollar(row.associate_commission_to_payout),
    row.commission_earned_year || "",
    formatDollar(row.accumulated_gci_after),
    formatDollar(row.associate_commission_to_payout),
    formatRate(row.gci_rate),
    formatDollar(row.house_amount)
];

const GCIBoard = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [nameFilter, setNameFilter] = useState("");
    const [rsidFilter, setRSIDFilter] = useState("");
    const [invoiceFilter, setInvoiceFilter] = useState("");
    const [sortColumn, setSortColumn] = useState(3);
    const [sortDirection, setSortDirection] = useState("asc");

    const loadData = async () => {
        setLoading(true);
        setError("");

        try {
            const response = await axios_gci.get("/simulations/board");
            console.log('response', response.data);
            const rows = response.data && Array.isArray(response.data.rows)
                ? response.data.rows
                : [];
            setData(rows.map(createTableRow));
        } catch (requestError) {
            console.error("Unable to load the new GCI simulation board", requestError);
            setData([]);
            setError(
                requestError.response &&
                requestError.response.data &&
                requestError.response.data.error
                    ? requestError.response.data.error
                    : "Unable to load the GCI simulation board."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const sortedData = useMemo(() => {
        const name = normalize(nameFilter.trim());
        const rsId = normalize(rsidFilter.trim());
        const invoice = normalize(invoiceFilter.trim());

        const filtered = data.filter(row => {
            return (!name || normalize(row[0]).includes(name)) &&
                (!rsId || normalize(row[2]).includes(rsId)) &&
                (!invoice || normalize(row[INVOICE_COLUMN_INDEX]).includes(invoice));
        });

        return filtered.slice().sort((a, b) => compareRows(a, b, sortColumn, sortDirection));
    }, [data, nameFilter, rsidFilter, invoiceFilter, sortColumn, sortDirection]);

    return (
        <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
                <h1>GCI Board</h1>
            </GridItem>

            <GridItem xs={12} sm={4} md={4}>
                <CustomInput
                    onChange={event => setNameFilter(event.target.value)}
                    value={nameFilter}
                    label="Filter by Name"
                />
            </GridItem>

            <GridItem xs={12} sm={4} md={4}>
                <CustomInput
                    onChange={event => setRSIDFilter(event.target.value)}
                    value={rsidFilter}
                    label="Filter by RS ID"
                />
            </GridItem>

            <GridItem xs={12} sm={4} md={4}>
                <CustomInput
                    onChange={event => setInvoiceFilter(event.target.value)}
                    value={invoiceFilter}
                    label="Filter by Invoice Number"
                />
            </GridItem>

            <GridItem xs={12} sm={12} md={12}>
                <IconButton disabled={loading} onClick={loadData}>
                    <Cached />
                </IconButton>
                {loading && <span>Loading GCI simulation...</span>}
                {error && <div style={{ color: "#c00" }}>{error}</div>}
            </GridItem>

            <GridItem xs={12} sm={8} md={8}>
                <div style={{ display: "flex", alignItems: "center", margin: "5px 0 20px" }}>
                    <span style={{ marginRight: 12 }}>Sort by</span>
                    <select
                        value={sortColumn}
                        onChange={event => setSortColumn(Number(event.target.value))}
                        style={{
                            height: 38,
                            minWidth: 260,
                            border: "1px solid #ddd",
                            borderRadius: 4,
                            background: "#fff",
                            padding: "0 10px"
                        }}
                    >
                        {TABLE_HEAD.map((heading, index) => (
                            <option key={heading} value={index}>
                                {heading}
                            </option>
                        ))}
                    </select>
                    <IconButton
                        onClick={() => setSortDirection(sortDirection === "asc" ? "desc" : "asc")}
                        title={sortDirection === "asc" ? "Ascending" : "Descending"}
                    >
                        {sortDirection === "asc" ? <ArrowUpward /> : <ArrowDownward />}
                    </IconButton>
                </div>
            </GridItem>

            <GridItem xs={12} sm={4} md={4}>
                <div style={{ margin: "14px 0 20px", textAlign: "right" }}>
                    Showing {sortedData.length} rows
                </div>
            </GridItem>

            <GridItem xs={12} sm={12} md={12}>
                <Table tableHead={TABLE_HEAD} tableData={sortedData} />
            </GridItem>
        </GridContainer>
    );
};

export default GCIBoard;
